function resolveApiBase(): string {
  let url = import.meta.env.VITE_API_URL || ""
  // Ensure protocol is present
  if (url && !url.startsWith("http://") && !url.startsWith("https://")) {
    url = `https://${url}`
  }
  // Remove trailing slash
  return `${url.replace(/\/$/, "")}/api/publisher-portal`
}

const API_BASE = resolveApiBase()

let accessToken: string | null = null
let refreshPromise: Promise<boolean> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

class ApiError extends Error {
  code: number
  constructor(message: string, code: number) {
    super(message)
    this.code = code
    this.name = "ApiError"
  }
}

async function attemptRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem("portal_refresh_token")
  if (!refreshToken) return false

  try {
    const res = await fetch(`${API_BASE}/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false
    const data = await res.json()
    accessToken = data.access_token
    localStorage.setItem("portal_refresh_token", data.refresh_token)
    return true
  } catch {
    return false
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const url = `${API_BASE}${path}`
  const headers: Record<string, string> = {}

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`
  }
  if (body) {
    headers["Content-Type"] = "application/json"
  }

  let res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  // On 401, try refresh once
  if (res.status === 401 && accessToken) {
    if (!refreshPromise) {
      refreshPromise = attemptRefresh()
    }
    const refreshed = await refreshPromise
    refreshPromise = null

    if (refreshed) {
      headers["Authorization"] = `Bearer ${accessToken}`
      res = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      })
    } else {
      // Refresh failed — force logout
      accessToken = null
      localStorage.removeItem("portal_refresh_token")
      window.location.href = "/login"
      throw new ApiError("Session expired", 401)
    }
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({ detail: "Request failed" }))
    throw new ApiError(data.detail || `Error ${res.status}`, res.status)
  }

  return res.json()
}

export function apiGet<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  let url = path
  if (params) {
    const searchParams = new URLSearchParams()
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== null && val !== "") {
        searchParams.set(key, String(val))
      }
    }
    const qs = searchParams.toString()
    if (qs) url += `?${qs}`
  }
  return request<T>("GET", url)
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body)
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body)
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>("DELETE", path)
}
